import { useExtensionUpdateNotice } from "@/components/features/ClubImport/useExtensionUpdateNotice";
import { Dialog } from "@/design-system/Dialog/Dialog";
import { sendMessageToExtension } from "@/functions/sendMessageToExtension";
import { selectChromeExtensionBelow } from "@/redux/clubImport/clubImportSlice";
import { addAlert, showDialog, showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector } from "@/redux/user/userSlice";
import { EA_WEB_APP_URL } from "@/settings";
import { safeGetJSON, safeRemoveItem, safeSetItem } from "@/utils/safeStorage";
import { type CollectedSync, getCollectedSync } from "@repo/shared/api";
import {
  GALLERY_STORAGE_KEYS,
  describeCollectedSync,
} from "@repo/shared/gallery";
import { useTranslation } from "@repo/shared/i18n";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { GalleryStepsProps } from "./GallerySteps";

const POLL_MS = 1000;
// The first extension release whose sync starts after an EA login.
const MIN_SYNC_EXTENSION_VERSION = [2, 4, 21] as const;
// Time to open and log in to the EA web app before its first page arrives.
const WAIT_FOR_EA_MS = 10 * 60_000;

type Run = {
  startedAt: number;
  /** The sync's last page before Start; a different one is this run's. */
  lastBatchAt?: string;
};

/**
 * Refresh Collected Players: the dialog, the extension's sync over every EA
 * player concept and the toast it ends in. The extension's pages carry no run
 * id, so this run is the one whose pages land after Start.
 */
export const useCollectedPlayersSync = (onSynced: () => void) => {
  const { t } = useTranslation("gallery");
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(getUserSelector);
  const showExtensionUpdateNotice = useExtensionUpdateNotice();
  const extensionTooOld = useSelector(
    selectChromeExtensionBelow(MIN_SYNC_EXTENSION_VERSION),
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  // Stored, so the other Gallery page picks the run up after a navigation; the poll ends a stale one.
  const [run, setRun] = useState<Run | undefined>(
    () => safeGetJSON<Run>(GALLERY_STORAGE_KEYS.collectedSyncRun) ?? undefined,
  );
  const [progress, setProgress] = useState(0);
  const track = (next?: Run) => {
    setRun(next);
    if (next)
      safeSetItem(GALLERY_STORAGE_KEYS.collectedSyncRun, JSON.stringify(next));
    else safeRemoveItem(GALLERY_STORAGE_KEYS.collectedSyncRun);
  };

  useEffect(() => {
    if (!run) return;
    let cancelled = false;
    let timeout: number | undefined;
    const poll = () => {
      getCollectedSync()
        .then((sync) => {
          if (cancelled) return;
          const ours =
            !!sync.lastBatchAt && sync.lastBatchAt !== run.lastBatchAt;
          if (ours && sync.status === "completed") {
            track(undefined);
            onSynced();
            dispatch(
              addAlert({
                type: "success",
                title: t("sync.updated"),
                description: t("sync.counts", {
                  count: sync.collectedCount,
                }),
              }),
            );
          } else if (
            (ours && sync.status === "paused") ||
            (!ours && Date.now() - run.startedAt > WAIT_FOR_EA_MS)
          ) {
            track(undefined);
          } else {
            if (ours) setProgress(sync.progress);
            timeout = window.setTimeout(poll, POLL_MS);
          }
        })
        .catch(() => {
          if (!cancelled) timeout = window.setTimeout(poll, POLL_MS);
        });
    };
    poll();
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
    // A run's own callbacks; onSynced only bumps a counter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, t, dispatch]);

  const open = () => {
    if (!user.uuid) {
      dispatch(showModal({ modalType: "login-required" }));
      return;
    }
    // Locked below the minimum: the update pop-up without Continue.
    if (extensionTooOld) {
      dispatch(showDialog({ modalType: "extension-update-notice", data: {} }));
      return;
    }
    setDialogOpen(true);
  };

  const start = async () => {
    setDialogOpen(false);
    let before;
    try {
      // Pages from a user without a club are rejected, so check before EA opens.
      before = await getCollectedSync();
    } catch {
      return;
    }
    if (!before.clubImported) return;
    showExtensionUpdateNotice(async () => {
      try {
        await sendMessageToExtension({
          user,
          collectedPlayersSyncRequest: true,
        });
      } catch {
        return;
      }
      window.open(EA_WEB_APP_URL, "_blank");
      setProgress(0);
      track({ startedAt: Date.now(), lastBatchAt: before.lastBatchAt });
    });
  };

  const ui = (
    <Dialog
      isOpen={dialogOpen}
      onClose={() => setDialogOpen(false)}
      title={t("sync.title")}
      posLabel={t("sync.start")}
      onPositive={() => void start()}
      onNegative={() => setDialogOpen(false)}
    >
      <p>{t("sync.dialogIntro")}</p>
      <ol className="mt-2 list-decimal space-y-1 pl-5">
        <li>{t("sync.step1")}</li>
        <li>{t("sync.step2")}</li>
        <li>{t("sync.step3")}</li>
      </ol>
      <p className="mt-3">{t("sync.autoUpdateNote")}</p>
      <p className="mt-2 font-semibold">{t("sync.openPacksNote")}</p>
    </Dialog>
  );

  return { open, running: !!run, progress, ui };
};

/** GallerySteps' Sync card from the sync hook and the last sync read; the same on every Gallery page. */
export const galleryStepsSync = (
  sync: ReturnType<typeof useCollectedPlayersSync>,
  collectedSync: CollectedSync | undefined,
  syncedThisVisit: boolean,
  language: string,
): GalleryStepsProps["sync"] => {
  const state = describeCollectedSync(collectedSync, sync.running, Date.now());
  return {
    running: sync.running,
    progress: sync.progress,
    done: state.done,
    syncedThisVisit,
    collected: state.collected,
    nextSyncAt: state.nextSyncAt?.toLocaleTimeString(language, {
      hour: "2-digit",
      minute: "2-digit",
    }),
    onOpen: sync.open,
  };
};
